import React, { useState } from 'react';
import { Play, ExternalLink, Clock, Layers } from 'lucide-react';
import { resolveMediaUrl, handleImageError } from '../../../utils/mediaResolver';

export default function BannerPreview({ form = {}, viewport = 'desktop' }) {
  const [deviceMode, setDeviceMode] = useState(viewport === 'mobile' ? 'mobile' : 'desktop');

  const placement = form.placement || 'hero';
  const alignment = form.alignment || 'left';
  const showOverlay = form.overlay !== false;

  const currentImage = (deviceMode === 'mobile' && form.mobile_image)
    ? form.mobile_image
    : (form.image || form.mobile_image || '');

  const alignClass = alignment === 'center'
    ? 'text-center items-center'
    : alignment === 'right'
    ? 'text-right items-end'
    : 'text-left items-start';

  return (
    <div className="bg-slate-50 min-h-full flex flex-col font-sans">
      {/* Top Banner Meta Bar */}
      <div className="bg-white border-b px-4 py-2.5 flex flex-wrap items-center justify-between gap-2 text-xs">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-slate-500">Placement:</span>
          <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-bold uppercase text-[11px]">
            {placement}
          </span>
          {form.group_key && (
            <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-[11px] font-mono">
              Group: {form.group_key}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Mobile vs Desktop Image Toggle */}
          <div className="flex bg-slate-100 p-0.5 rounded-lg border text-[11px]">
            <button
              type="button"
              onClick={() => setDeviceMode('desktop')}
              className={`px-2.5 py-0.5 rounded font-medium transition ${
                deviceMode === 'desktop' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
              }`}
            >
              Desktop Image
            </button>
            <button
              type="button"
              onClick={() => setDeviceMode('mobile')}
              className={`px-2.5 py-0.5 rounded font-medium transition ${
                deviceMode === 'mobile' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
              }`}
            >
              Mobile Image
            </button>
          </div>

          {form.autoplay_ms && (
            <span className="flex items-center gap-1 text-[11px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
              <Clock className="w-3 h-3" />
              {form.autoplay_ms}ms
            </span>
          )}
        </div>
      </div>

      {/* Main Banner Canvas */}
      <div className="p-6 md:p-8 flex-1 flex flex-col justify-center items-center">
        <div
          className={`w-full relative rounded-2xl overflow-hidden shadow-xl border bg-slate-900 ${
            placement === 'hero'
              ? deviceMode === 'mobile' ? 'max-w-xs h-[480px]' : 'max-w-4xl h-[380px]'
              : 'max-w-2xl h-[240px]'
          }`}
        >
          {/* Banner Media (Image or Video) */}
          {form.video ? (
            <div className="absolute inset-0 flex items-center justify-center bg-black">
              <video
                src={form.video}
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              />
              <div className="absolute top-3 right-3 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1">
                <Play className="w-2.5 h-2.5" /> Video Banner
              </div>
            </div>
          ) : currentImage ? (
            <img
              src={resolveMediaUrl(currentImage)}
              alt={form.alt || form.heading || 'Banner'}
              className="w-full h-full object-cover"
              onError={handleImageError}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-r from-indigo-900 to-[#2B3380] text-white/50 text-sm">
              Upload banner image or paste media URL
            </div>
          )}

          {/* Optional Text Overlay */}
          {showOverlay && (
            <div
              className={`absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6 md:p-10 flex flex-col justify-end ${alignClass}`}
            >
              {form.badge && (
                <span className="inline-block bg-[#2B3380] text-white text-[10px] md:text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-2 shadow">
                  {form.badge}
                </span>
              )}
              {form.subheading && (
                <span className="text-white/80 text-xs md:text-sm font-semibold tracking-wide block mb-1">
                  {form.subheading}
                </span>
              )}
              <h2 className="text-xl md:text-3xl font-extrabold text-white leading-tight max-w-xl">
                {form.heading || <span className="text-white/40 italic">Banner Headline</span>}
              </h2>
              {form.description && (
                <p className="text-white/70 text-xs md:text-sm mt-2 max-w-lg line-clamp-2">
                  {form.description}
                </p>
              )}
              {(form.button_text || form.button_url) && (
                <div className="mt-4">
                  <span className="inline-flex items-center gap-1.5 bg-white text-[#2B3380] font-bold text-xs md:text-sm px-5 py-2.5 rounded-xl shadow-lg hover:bg-slate-100 transition">
                    {form.button_text || 'Learn More'}
                    {form.external && <ExternalLink className="w-3.5 h-3.5 ml-0.5" />}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Banner Details summary */}
        <div className="mt-6 text-xs text-slate-500 max-w-lg text-center space-y-1">
          <p>
            Target URL:{' '}
            <strong className="text-slate-800 font-mono">
              {form.button_url || 'None (Banner only)'}
            </strong>
          </p>
          <p>
            Image Mode: <strong>{deviceMode === 'mobile' ? 'Mobile Image' : 'Desktop Image'}</strong>
            {form.mobile_image ? ' (Dedicated mobile image configured)' : ' (Using desktop image fallback for mobile)'}
          </p>
        </div>
      </div>
    </div>
  );
}
